import { useLabInsightChat } from "~vue/composables/useLabInsightChat";
import { useLabInsightFeedback } from "~vue/composables/useLabInsightFeedback";
import type { LabInsightLocation } from "~vue/types/labs";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, type MaybeRefOrGetter, onMounted, toValue } from "vue";

export type { LabInsightLocation };

/**
 * Pass `fetchExistingFeedback: false` where no rating control is shown, to skip the unused mount-time
 * feedback request for each reading.
 *
 * Pass `initialFeedbackValue` when the caller already knows the answer from a server-rendered prop
 * (e.g. `LabInsightWithFeedback.feedback`), so the feedback control renders in its final state on
 * first paint instead of animating into it once the mount-time fetch resolves.
 */
export function useLabInsightActions(
    insightId: string,
    location: LabInsightLocation = "mywork",
    { fetchExistingFeedback = true, initialFeedbackValue = null }: { fetchExistingFeedback?: boolean; initialFeedbackValue?: MaybeRefOrGetter<number | null> } = {},
) {
    // Feedback
    const { fetchFeedback, feedbackFetching, feedbackData, feedbackSubmitting, submitFeedback, submitError } = useLabInsightFeedback(insightId);

    const feedbackValue = computed(() => (feedbackData.value === null ? toValue(initialFeedbackValue) : (feedbackData.value.feedback?.value ?? null)));
    const feedbackTags = computed(() => feedbackData.value?.feedback?.tags ?? []);
    const feedbackComment = computed(() => feedbackData.value?.feedback?.comment ?? null);

    async function handleFeedbackSelect(value: 0 | 1) {
        return await submitFeedback({ insight_id: insightId, value, comment: null, tags: [], location });
    }

    async function handleSubmitFeedback({ tags, comment, value }: { tags: string[]; comment: string | null; value: 0 | 1 }) {
        // Submit through the full feedback endpoint (rather than the tags-only endpoint) so the
        // comment persists. The rating comes from the component's pending value (not the fetched
        // computed, which may not have updated yet) so the submitted value matches what the user
        // clicked; since it equals the current rating the backend does not treat this as a value
        // change (which would clear tags).
        await submitFeedback({ insight_id: insightId, value, comment, tags, location });
    }

    onMounted(async () => {
        if (fetchExistingFeedback) {
            await fetchFeedback();
        }
    });

    const { isStartingChat, chatError, handleStartChat: startLabInsightChat } = useLabInsightChat(insightId);

    async function handleStartChat() {
        if (isStartingChat.value) {
            return null;
        }
        // Preserve the existing My Work/homepage event while the New Chat prompt uses
        // useLabInsightChat directly with its own enum-backed analytics.
        void logUserInteraction(AmplitudeEventNames.INSIGHT_DIVE_DEEPER_CLICKED, { insightId, location });
        return startLabInsightChat();
    }

    return {
        // Feedback
        feedbackValue,
        feedbackTags,
        feedbackComment,
        feedbackData,
        feedbackFetching,
        feedbackSubmitting,
        submitError,
        handleFeedbackSelect,
        handleSubmitFeedback,

        // Chat
        isStartingChat,
        chatError,
        handleStartChat,
    };
}
